import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { formatBytes, formatClock } from './addedAssetFacts';
import { addedAssetsMessages as messages } from './addedAssetsMessages';
import type { AddedUploadState } from './addedUploadStore';
import styles from './UploadProgressCard.module.scss';

interface UploadProgressCardProps {
  upload: AddedUploadState;
  songTitle: string;
  songDurationSec: number | null;
}

/**
 * The upload on the way (design a4 `.upl`, `.uses`): the file, its size and length, the bytes sent
 * and the bar, then the checks: the length against the song, and for a music video the sound check
 * that runs after the upload.
 */
const UploadProgressCardComponent: React.FC<UploadProgressCardProps> = ({ upload, songTitle, songDurationSec }) => {
  const intl = useIntl();
  const { measures, sizeBytes, sentBytes } = upload;
  const percent = sizeBytes > 0 ? Math.min(100, Math.floor((sentBytes / sizeBytes) * 100)) : 0;
  const length = formatClock(measures?.durationSeconds ?? null);
  const songLength = formatClock(songDurationSec);

  const lengthLine = useMemo(() => {
    const differs = upload.warnings.find((warning) => warning.kind === 'lengthDiffers');
    if (differs !== undefined) {
      return {
        warn: true,
        text: intl.formatMessage(messages.lengthDiffers, {
          video: formatClock(differs.videoSec),
          song: formatClock(differs.songSec),
        }),
      };
    }
    if (measures !== null && songLength !== null && upload.themeId !== 'canvasLoop') {
      return { warn: false, text: intl.formatMessage(messages.lengthMatches, { length: songLength }) };
    }
    return null;
  }, [intl, measures, songLength, upload.themeId, upload.warnings]);

  return (
    <>
      <div className={styles.upl} data-testid="added-upload-progress">
        <div className={styles.uplHead}>
          <span className={styles.uplText}>
            <b>{upload.fileName}</b>
            {measures !== null && length !== null && (
              <span>
                {intl.formatMessage(messages.measures, {
                  width: measures.width,
                  height: measures.height,
                  length,
                })}
              </span>
            )}
          </span>
          <small>
            {intl.formatMessage(messages.sizeLine, { sent: formatBytes(sentBytes), total: formatBytes(sizeBytes) })}
          </small>
        </div>
        <div
          className={styles.bar}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label={intl.formatMessage(messages.uploading)}
        >
          {/* The width is the data: the share of the bytes that S3 got. */}
          <i style={{ width: `${percent}%` }} />
        </div>
      </div>
      <div className={styles.checks}>
        {lengthLine !== null && (
          <span className={clsx(lengthLine.warn ? styles.checkWarn : styles.checkOk)}>{lengthLine.text}</span>
        )}
        {upload.themeId === 'musicVideo' && upload.phase !== 'done' && (
          <span>{intl.formatMessage(messages.soundCheckSoon, { title: songTitle })}</span>
        )}
        {upload.phase === 'checking' && <span>{intl.formatMessage(messages.checking)}</span>}
        {upload.phase === 'done' && (
          <span className={styles.checkOk} role="status">
            {intl.formatMessage(messages.added)}
          </span>
        )}
      </div>
    </>
  );
};

export const UploadProgressCard = TypedMemo(UploadProgressCardComponent);
